import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import i18n from "/src/locals/i18n";

// Nucleo Icons
//import "./assets/css/nucleo-icons.css";
//import "./assets/css/nucleo-svg.css";
// Leaflet and markercluster CSS are now dynamically imported only where needed
// Bootstrap CSS ya se carga vía material-kit.scss → @import "material-kit/bootstrap/bootstrap"

import "./assets/css/flag-icons-custom.css";
import "./assets/css/bootstrap-icons-custom.css";

// Bootstrap JS – solo los plugins necesarios (tree-shakeable)
import "bootstrap/js/dist/alert"; // data-bs-dismiss="alert"
import "bootstrap/js/dist/collapse"; // NavbarDefault (también importa ESM)
import "bootstrap/js/dist/dropdown"; // NavbarDefault (también importa ESM)
import "bootstrap/js/dist/tooltip"; // ExampleCard data-bs-toggle="tooltip"

// Font Awesome — solo los iconos que se usan en el proyecto
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// fas (solid)
import {
  faShareAlt,
  faCalendarPlus,
  faCalendarCheck,
  faClock,
  faMap,
  faMapMarkerAlt,
  faHandPeace,
  faEuroSign,
  faUser,
  faCamera,
  faUserFriends,
  faMapMarkedAlt,
  faHeart,
  faChevronDown,
  faChevronUp,
  faUsers,
  faEnvelope,
  faPhone,
  faRefresh,
  faStar,
  faSearch,
  faFilterCircleXmark,
  faFilter,
  faArrowLeft,
  faLocationDot,
  faCalendar,
  faTimes,
  faPen,
  faTrashAlt,
  faInfoCircle,
  faPlay,
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faCrown,
  faMusic,
  faMasksTheater,
  faPersonWalking,
  faWandMagicSparkles,
  faPalette,
  faBook,
  faVolleyball,
  faThLarge,
  faNewspaper,
  faBuilding,
  faGlobe,
  faMartiniGlass,
  faGuitar,
  faHandshake,
  faLandmark,
  faPause,
  faPlus,
  faCloudArrowUp,
  faFilePdf,
  faImage,
  faArrowUpRightFromSquare,
  faAddressBook,
  faNoteSticky,
  faArrowsRotate,
  faUserPlus,
  faInbox,
  faListCheck,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

// fab (brands)
import {
  faInstagram,
  faFacebook,
  faFacebookF,
  faYoutube,
  faSpotify,
  faSoundcloud,
  faXTwitter,
  faLinkedinIn,
  faTiktok,
  faTwitter,
  faDribbble,
  faGithub,
  faGooglePlus,
  faPinterest,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";

// far (regular) — aliases para los que comparten nombre con fas
import {
  faEye,
  faEyeSlash,
  faHandPeace as farHandPeace,
  faHeart as farHeart,
  faStar as farStar,
} from "@fortawesome/free-regular-svg-icons";

library.add(
  // fas
  faShareAlt,
  faCalendarPlus,
  faCalendarCheck,
  faClock,
  faMap,
  faMapMarkerAlt,
  faHandPeace,
  faEuroSign,
  faUser,
  faCamera,
  faUserFriends,
  faMapMarkedAlt,
  faHeart,
  faChevronDown,
  faChevronUp,
  faUsers,
  faEnvelope,
  faPhone,
  faRefresh,
  faStar,
  faSearch,
  faFilterCircleXmark,
  faFilter,
  faArrowLeft,
  faLocationDot,
  faCalendar,
  faTimes,
  faPen,
  faTrashAlt,
  faInfoCircle,
  faPlay,
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faCrown,
  faMusic,
  faMasksTheater,
  faPersonWalking,
  faWandMagicSparkles,
  faPalette,
  faBook,
  faVolleyball,
  faThLarge,
  faNewspaper,
  faBuilding,
  faGlobe,
  faMartiniGlass,
  faGuitar,
  faHandshake,
  faLandmark,
  faPause,
  faPlus,
  faCloudArrowUp,
  faFilePdf,
  faImage,
  faArrowUpRightFromSquare,
  faAddressBook,
  faNoteSticky,
  faArrowsRotate,
  faUserPlus,
  faInbox,
  faListCheck,
  faCheck,
  // fab
  faInstagram,
  faFacebook,
  faFacebookF,
  faYoutube,
  faSpotify,
  faSoundcloud,
  faXTwitter,
  faLinkedinIn,
  faTiktok,
  faTwitter,
  faDribbble,
  faGithub,
  faGooglePlus,
  faPinterest,
  faWhatsapp,
  // far
  faEye,
  faEyeSlash,
  farHandPeace,
  farHeart,
  farStar
);

// MATERIAL KIT
import materialKit from "./material-kit";

// 🧠 Head Management
import { createHead } from "@vueuse/head";

const app = createApp(App);
const head = createHead();

// 👉 Control de cookies: cargar GA diferido para no competir con FCP/LCP
const isLocalhost = window.location.hostname === "localhost";
const GA_ID = "G-H389FYRHS7";

const initGA = () => {
  if (isLocalhost || document.getElementById("google-analytics-script")) return;

  const gaScript = document.createElement("script");
  gaScript.id = "google-analytics-script";
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(gaScript);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_ID, { send_page_view: false });

  if (router) {
    router.afterEach((to) => {
      gtag("config", GA_ID, {
        page_path: to.fullPath,
        location: window.location.origin + to.fullPath,
      });
    });
  }
};

// Diferir GA hasta que el navegador esté idle (no bloquea FCP/LCP)
if ("requestIdleCallback" in window) {
  requestIdleCallback(initGA);
} else {
  setTimeout(initGA, 3000);
}

app.component("font-awesome-icon", FontAwesomeIcon);
app.use(createPinia());
app.use(router);
app.use(materialKit);
app.use(i18n);
app.use(head);
app.mount("#app");
